import { createContext, useContext, useMemo } from "react";

/**
 * Panel mode — "light" | "dark".
 *
 * Deliberately not an app-wide theme. It swaps the canvas token scope only:
 * the center panel, composer and locked-output body. The sidebar and panel
 * headers keep their fixed chrome palette in both modes, which is why the UI
 * calls this "Dark panel" and never "Dark mode".
 *
 * App owns the state (and the Supabase write). This exists so the header
 * toggle and the Settings toggle read the same value without threading props
 * through every panel — they are the same control in two places, so they must
 * not be able to disagree.
 */
const PanelModeContext = createContext({ panelMode: "light", setPanelMode: () => {} });

export function PanelModeProvider({ panelMode, setPanelMode, children }) {
  // App re-renders on every keystroke in the composer; without this the value
  // identity changes each time and drags all consumers along with it.
  const value = useMemo(() => ({ panelMode, setPanelMode }), [panelMode, setPanelMode]);
  return <PanelModeContext.Provider value={value}>{children}</PanelModeContext.Provider>;
}

export const usePanelMode = () => useContext(PanelModeContext);
